import { useState, useEffect, useCallback, useRef } from 'react'
import ContactsTable from '../todolist/ContactsTable'
import SelectAllFilteredBanner from '../todolist/SelectAllFilteredBanner'
import BulkAddToCampaignPicker from '../campaigns/BulkAddToCampaignPicker'
import FilterChipsBar from './FilterChipsBar'
import DateRangeChip from './chips/DateRangeChip'
import IncludeExcludeIconChip from './chips/IncludeExcludeIconChip'
import IncludeExcludeFlagChip from './chips/IncludeExcludeFlagChip'
import ExcludeCampaignChip from './chips/ExcludeCampaignChip'
import AgentFilterChip from './chips/AgentFilterChip'
import useCampaignBuilderFilters from '../../hooks/useCampaignBuilderFilters'
import { campaignBuilderAPI } from '../../services/api'

const COLUMNS = ['checkbox', 'created_at', 'icon', 'bandera', 'name', 'phone', 'assigned_to', 'campaigns']
const PAGE_SIZE = 50
const SORT_DEFAULT = []

export default function CampaignBuilderView({ user }) {
  const isAdmin = !!user?.is_admin
  const agentName = user?.name || 'anon'

  const {
    filters, updateFilter, resetFilters,
    presentKeys, addFilterKey, removeFilterKey, justAddedKey, clearJustAdded,
  } = useCampaignBuilderFilters(agentName)

  const [contacts, setContacts] = useState([])
  const contactsRef = useRef([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const offsetRef = useRef(0)
  const [selectedIds, setSelectedIds] = useState([])
  const [selectAllFiltered, setSelectAllFiltered] = useState(false)
  const [showCampaign, setShowCampaign] = useState(false)
  // Orden por columnas (mismo contrato que TodoList: array {column,direction}, max 2)
  const [sortConfig, setSortConfig] = useState(SORT_DEFAULT)
  const sortRef = useRef(SORT_DEFAULT)
  const [sortLoadingColumn, setSortLoadingColumn] = useState(null)

  const loadContacts = useCallback(async (append = false) => {
    // Solo mostrar spinner full-pane en carga inicial (grilla vacia). En re-fetch por
    // orden o filtros con filas ya presentes, mantener la grilla montada para evitar
    // parpadeo (mismo patron que TodoListView): setContacts reemplaza filas en sitio.
    if (append) setLoadingMore(true)
    else if (contactsRef.current.length === 0) setLoading(true)
    try {
      const offset = append ? offsetRef.current : 0
      const sort = sortRef.current.map(s => `${s.column}:${s.direction}`).join(',')
      const res = await campaignBuilderAPI.getContacts({ ...filters, sort, limit: PAGE_SIZE, offset })
      const newContacts = res?.contacts || []
      const newTotal = res?.total || 0
      setContacts(prev => append ? [...prev, ...newContacts] : newContacts)
      setTotal(newTotal)
      offsetRef.current = offset + newContacts.length
      setHasMore(offsetRef.current < newTotal)
    } catch (err) {
      console.error('[CampaignBuilder] fetch error:', err)
    } finally {
      if (append) setLoadingMore(false)
      else setLoading(false)
    }
  }, [filters])

  // Mantener contactsRef sincronizado para el guard anti-parpadeo de loadContacts
  useEffect(() => { contactsRef.current = contacts }, [contacts])

  // Fresh load (debounce 300ms) al montar / cambiar filtros
  useEffect(() => {
    const timer = setTimeout(() => loadContacts(false), 300)
    return () => clearTimeout(timer)
  }, [loadContacts])

  // Reset selección al cambiar filtros
  useEffect(() => {
    setSelectedIds([])
    setSelectAllFiltered(false)
  }, [filters])

  // Limpiar justAddedKey (consumido por el chip recien montado)
  useEffect(() => {
    if (!justAddedKey) return
    const t = setTimeout(clearJustAdded, 0)
    return () => clearTimeout(t)
  }, [justAddedKey, clearJustAdded])

  // Orden: ciclo 3 estados (desc->asc->off; icon_current asc->desc->off), max 2 columnas
  const handleSortChange = useCallback((column) => {
    const current = sortRef.current
    const idx = current.findIndex(s => s.column === column)
    let next
    if (idx === -1) {
      const defaultDir = column === 'icon_current' ? 'asc' : 'desc'
      next = [...current, { column, direction: defaultDir }]
      if (next.length > 2) next = next.slice(-2)
    } else {
      const s = current[idx]
      const startDir = column === 'icon_current' ? 'asc' : 'desc'
      const oppositeDir = startDir === 'asc' ? 'desc' : 'asc'
      if (s.direction === startDir) { next = [...current]; next[idx] = { column, direction: oppositeDir } }
      else { next = current.filter((_, i) => i !== idx) }
    }
    sortRef.current = next
    setSortConfig(next)
    setSortLoadingColumn(next[0]?.column || null)
    loadContacts(false).finally(() => setSortLoadingColumn(null))
  }, [loadContacts])

  // Reset SOLO del orden por columnas (no toca los filtros; el Constructor ya tiene
  // su propio "Limpiar filtros"). sortConfig=[] -> backend cae en su default creado_en DESC.
  const handleSortReset = useCallback(() => {
    sortRef.current = []
    setSortConfig([])
    setSortLoadingColumn(null)
    offsetRef.current = 0
    loadContacts(false)
  }, [loadContacts])

  const renderChip = (key, filtersArg, updateFilterArg) => {
    const autoOpen = justAddedKey === key
    const onRemove = () => removeFilterKey(key)
    const common = { key, filters: filtersArg, updateFilter: updateFilterArg, autoOpen, onRemove }
    switch (key) {
      case 'date': return <DateRangeChip {...common} />
      case 'include_ic': return <IncludeExcludeIconChip mode="include" {...common} />
      case 'exclude_ic': return <IncludeExcludeIconChip mode="exclude" {...common} />
      case 'include_flag': return <IncludeExcludeFlagChip mode="include" {...common} />
      case 'exclude_flag': return <IncludeExcludeFlagChip mode="exclude" {...common} />
      case 'exclude_camp': return <ExcludeCampaignChip {...common} />
      case 'agent': return <AgentFilterChip {...common} />
      default: return null
    }
  }

  const handleSelectAllFiltered = async () => {
    try {
      const res = await campaignBuilderAPI.getContactIds(filters)
      setSelectedIds(res?.ids || [])
      setSelectAllFiltered(true)
    } catch (err) {
      console.error('[CampaignBuilder] select all error:', err)
    }
  }

  const handleClearAll = () => {
    setSelectedIds([])
    setSelectAllFiltered(false)
  }

  const selectedCount = selectedIds.length

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-900 min-h-0 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">🛠️ Constructor de Campañas</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
          Mostrando {contacts.length} de {total}
          {selectedCount > 0 && ` · ${selectedCount} seleccionados`}
        </span>
        {sortConfig.length > 0 && (
          <button
            onClick={handleSortReset}
            type="button"
            className="px-2.5 py-1 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-semibold hover:bg-purple-50 dark:hover:bg-gray-700 transition shadow-sm flex items-center gap-1 whitespace-nowrap"
            title="Volver al orden predeterminado (fecha de creacion)"
          >
            <span>↺</span>
            <span>Resetear orden</span>
          </button>
        )}
        <input
          type="text"
          placeholder="🔍 Buscar nombre o teléfono…"
          value={filters.search || ''}
          onChange={e => updateFilter('search', e.target.value)}
          className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
        <button
          onClick={() => loadContacts(false)}
          type="button"
          className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200"
        >
          🔄 Refrescar
        </button>
        <div className="flex gap-2 ml-auto">
          <button
            disabled={selectedCount === 0}
            onClick={() => setShowCampaign(true)}
            type="button"
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded disabled:opacity-50 hover:bg-emerald-700"
          >
            + Campaña
          </button>
        </div>
      </div>

      {/* Filter chips bar */}
      <FilterChipsBar
        presentKeys={presentKeys}
        addFilterKey={addFilterKey}
        resetFilters={resetFilters}
        isAdmin={isAdmin}
        renderChip={renderChip}
        filters={filters}
        updateFilter={updateFilter}
      />

      {/* Select-all-filtered banner */}
      <SelectAllFilteredBanner
        visibleCount={contacts.length}
        totalCount={total}
        selectedCount={selectedCount}
        selectAllFiltered={selectAllFiltered}
        onSelectAll={handleSelectAllFiltered}
        onClearAll={handleClearAll}
      />

      {/* Contacts grid (read-only; ContactsTable maneja su propio scroll) */}
      <ContactsTable
        contacts={contacts}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={() => loadContacts(true)}
        total={total}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        visibleColumns={COLUMNS}
        readOnly={true}
        sortConfig={sortConfig}
        onSortChange={handleSortChange}
        sortLoadingColumn={sortLoadingColumn}
      />

      {/* Bulk Add to Campaign Modal */}
      {showCampaign && (
        <BulkAddToCampaignPicker
          contactIds={selectedIds}
          onClose={() => setShowCampaign(false)}
          onSuccess={() => {
            setShowCampaign(false)
            setSelectedIds([])
            setSelectAllFiltered(false)
            loadContacts(false)
          }}
        />
      )}
    </div>
  )
}
